import { Fragment, useState, useEffect, useRef } from "react";
import AdminLayout from "@/components/layout/AdminLayout";
import API, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Save, RefreshCw, Download, ChevronDown, Search, FileText, Copy, ChevronLeft, ChevronRight, Mail } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";

const CHECKLIST_PREFIX = {
  order: "order",
  received: "received",
};

function buildChecklistKey(productId, section, rawSignature) {
  return `${productId}-${CHECKLIST_PREFIX[section]}-${rawSignature}`;
}

function buildChecklistStateFromItems(items = []) {
  const next = { order: {}, received: {} };
  items.forEach((item) => {
    const monitoring = item.monitoring || {};
    const signatures = item.ordered_variants || [];
    const orderMap = monitoring.order_checklist || {};
    const receivedMap = monitoring.received_checklist || {};

    signatures.forEach((variantItem) => {
      const rawSignature = variantItem.signature;
      next.order[buildChecklistKey(item.id, "order", rawSignature)] = !!orderMap[rawSignature];
      next.received[buildChecklistKey(item.id, "received", rawSignature)] = !!receivedMap[rawSignature];
    });
  });
  return next;
}

function VariantChecklistAccordion({
  title,
  items = [],
  emptyLabel = "Belum ada varian terorder.",
  checkedMap = {},
  onToggle = () => {},
}) {
  const checkedCount = items.filter((variantItem) => !!checkedMap[variantItem.key]).length;
  const totalCount = items.length;
  const uncheckedCount = Math.max(totalCount - checkedCount, 0);

  return (
    <details className="group rounded-lg border border-stone-200 bg-white/90">
      <summary className="flex cursor-pointer list-none items-center justify-between gap-3 px-3 py-2 text-[11px] font-semibold uppercase tracking-wider text-stone-600">
        <div className="flex items-center gap-2 flex-wrap">
          <span>{title}</span>
          <span className="inline-flex min-w-6 justify-center rounded-full bg-stone-100 px-1.5 py-0.5 text-[10px] text-stone-600">
            {checkedCount}/{totalCount}
          </span>
          {totalCount > 0 && (
            <span className="text-[10px] normal-case tracking-normal text-stone-400">
              {checkedCount} centang, {uncheckedCount} belum
            </span>
          )}
        </div>
        <ChevronDown className="h-4 w-4 text-stone-400 transition-transform group-open:rotate-180" />
      </summary>
      <div className="border-t border-stone-100 px-3 py-2">
        {items.length ? (
          <div className="space-y-2">
            {items.map((variantItem) => (
              <label key={variantItem.key} className="flex cursor-pointer items-start gap-2 rounded-lg border border-stone-100 bg-stone-50/70 px-3 py-2">
                <input
                  type="checkbox"
                  checked={!!checkedMap[variantItem.key]}
                  onChange={(e) => onToggle(variantItem.key, e.target.checked)}
                  className="mt-0.5 rounded border-stone-300 text-[#8A9A86] focus:ring-[#8A9A86]"
                />
                <div className="min-w-0 flex-1">
                  <p className="font-medium normal-case tracking-normal text-stone-800 break-words">{variantItem.label || "Tanpa varian"}</p>
                  <div className="mt-1 flex flex-wrap gap-2 text-[11px] normal-case tracking-normal text-stone-500">
                    <span>Qty: <span className="font-semibold text-stone-800">{variantItem.quantity || 0}</span></span>
                  </div>
                </div>
              </label>
            ))}
          </div>
        ) : (
          <p className="text-[11px] text-stone-400">{emptyLabel}</p>
        )}
      </div>
    </details>
  );
}

const normalizeDateTimeLocalValue = (value) => {
  if (!value) return "";
  const text = String(value).trim();
  if (!text) return "";
  if (/^\d{4}-\d{2}-\d{2}$/.test(text)) return `${text}T23:59`;
  const match = text.match(/^(\d{4}-\d{2}-\d{2})[ T](\d{2}:\d{2})/);
  return match ? `${match[1]}T${match[2]}` : text;
};

const parsePoClosingAtWib = (value) => {
  const normalized = normalizeDateTimeLocalValue(value);
  if (!normalized) return null;
  const match = normalized.match(/^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})$/);
  if (!match) return null;
  const [, y, m, d, hh, mm] = match;
  return new Date(Date.UTC(Number(y), Number(m) - 1, Number(d), Number(hh) - 7, Number(mm), 0, 0));
};

const getCurrentWibDate = () => new Date(new Date().toLocaleString("en-US", { timeZone: "Asia/Jakarta" }));

const getEffectivePoStatus = (product, overrides = {}) => {
  const rawStatus = Number(overrides?.is_po_open ?? product?.is_po_open ?? 1);
  const stockType = overrides?.stock_type ?? product?.stock_type;
  const closingDate = overrides?.preorder_closing_date ?? product?.preorder_closing_date;

  if (rawStatus === 1 && stockType === "preorder" && closingDate) {
    const closeAtWib = parsePoClosingAtWib(closingDate);
    if (closeAtWib && getCurrentWibDate() > closeAtWib) return 0;
  }

  return rawStatus;
};

const getPoStatusMeta = (product, overrides = {}) => {
  const rawStatus = Number(overrides?.is_po_open ?? product?.is_po_open ?? 1);
  const statusValue = getEffectivePoStatus(product, overrides);
  const isAutoClosedByDate = rawStatus === 1 && statusValue === 0;
  const statusClass = statusValue === 5
    ? "bg-fuchsia-100 text-fuchsia-800 border-fuchsia-200"
    : statusValue === 2
      ? "bg-emerald-100 text-emerald-800 border-emerald-200"
      : statusValue === 3
        ? "bg-indigo-100 text-indigo-800 border-indigo-200"
        : statusValue === 4
          ? "bg-cyan-100 text-cyan-800 border-cyan-200"
          : statusValue === 0
            ? "bg-red-100 text-red-800 border-red-200"
            : "bg-amber-100 text-amber-800 border-amber-200";
  const statusLabel = statusValue === 5 ? "Ready di Gallery" : statusValue === 2 ? "Ready di Pusat" : statusValue === 3 ? "Production" : statusValue === 4 ? "Dikirim ke Pusat" : statusValue === 0 ? "Closed" : "Open";
  const selectOpenLabel = "Open";
  return { rawStatus, statusValue, statusClass, statusLabel, isAutoClosedByDate, selectOpenLabel };
};

const getPoStatusRank = (product) => getEffectivePoStatus(product) === 1 ? 0 : 1;

const getPoCloseTime = (product) => {
  if (!product?.preorder_closing_date) return Number.NEGATIVE_INFINITY;
  const closeAtWib = parsePoClosingAtWib(product.preorder_closing_date);
  const time = closeAtWib ? closeAtWib.getTime() : new Date(product.preorder_closing_date).getTime();
  return Number.isFinite(time) ? time : Number.NEGATIVE_INFINITY;
};

const formatPoCloseDateForTemplate = (value) => {
  const closeAtWib = parsePoClosingAtWib(value);
  if (!closeAtWib) return "TANPA TANGGAL";
  return new Intl.DateTimeFormat("id-ID", { day: "numeric", month: "long", year: "numeric", timeZone: "Asia/Jakarta" }).format(closeAtWib);
};

const getWibDateKey = (date) => {
  if (!date) return "";
  const parts = new Intl.DateTimeFormat("en-GB", {
    timeZone: "Asia/Jakarta",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(date).reduce((acc, part) => {
    acc[part.type] = part.value;
    return acc;
  }, {});
  return `${parts.year}-${parts.month}-${parts.day}`;
};

const getPoCloseDateKey = (value) => {
  const closeAtWib = parsePoClosingAtWib(value);
  return closeAtWib ? getWibDateKey(closeAtWib) : "";
};

const getWibRelativeDateKey = (offsetDays = 0) => getWibDateKey(new Date(Date.now() + (offsetDays * 24 * 60 * 60 * 1000)));

const getClosePoScarcityLine = (dateKey) => {
  const todayKey = getWibRelativeDateKey(0);
  const tomorrowKey = getWibRelativeDateKey(1);
  if (dateKey === todayKey) return "🔥 *TERAKHIR HARI INI — jangan sampai kelewat yaa* 🔥";
  if (dateKey === tomorrowKey) return "⏰ *CLOSE BESOK — amankan orderannya dari sekarang yaa*";
  return "";
};

const getPoDisplayName = (product) => {
  const name = String(product?.name || "").replace(/\s+by\s+.+$/i, "").trim();
  const brand = String(product?.brand_name || "").trim();
  const base = name || "Produk";
  return brand && brand !== "No Brand" ? `${base} by ${brand}` : base;
};

const sortProductsByClosePo = (items = []) => {
  return [...items].sort((a, b) => {
    const aStatusRank = getPoStatusRank(a);
    const bStatusRank = getPoStatusRank(b);

    if (aStatusRank !== bStatusRank) return aStatusRank - bStatusRank;

    const aDate = getPoCloseTime(a);
    const bDate = getPoCloseTime(b);

    if (aDate !== bDate) return bDate - aDate;
    return String(a?.name || "").localeCompare(String(b?.name || ""));
  });
};

export default function AdminPO() {
  const { t, locale } = useLanguage();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [currentPage, setCurrentPage] = useState(1);
  const [pagination, setPagination] = useState({ total: 0, totalPages: 1 });
  const [serverStatusCounts, setServerStatusCounts] = useState({ all: 0 });
  const itemsPerPage = 20;
  const [changedData, setChangedData] = useState({});
  const [savingId, setSavingId] = useState(null);
  const [checklistState, setChecklistState] = useState({ order: {}, received: {} });
  const [initialChecklistState, setInitialChecklistState] = useState({ order: {}, received: {} });
  const [expandedDesktopRows, setExpandedDesktopRows] = useState({});
  const [broadcastingId, setBroadcastingId] = useState(null);
  const autosaveTimersRef = useRef({});

  const fetchProducts = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.set("limit", String(itemsPerPage));
      params.set("skip", String((currentPage - 1) * itemsPerPage));
      if (debouncedSearch.trim()) params.set("search", debouncedSearch.trim());
      if (statusFilter !== "all") params.set("po_status", statusFilter);

      const { data } = await API.get(`/admin/po-monitoring?${params.toString()}`);
      const monitoringItems = data?.items || [];
      const mergedProducts = monitoringItems.map((item) => ({
        id: item.product_id,
        name: item.product_name,
        brand_id: item.brand_id,
        brand_name: item.brand_name,
        stock_type: "preorder",
        preorder_open_date: item.preorder_open_date,
        preorder_closing_date: item.preorder_closing_date,
        preorder_delivery_estimate: item.preorder_delivery_estimate,
        is_po_open: item.is_po_open,
        total_orders: item.total_orders,
        monitoring: item.monitoring,
        ordered_variants: item.ordered_variants || [],
      }));
      const nextChecklistState = buildChecklistStateFromItems(mergedProducts);
      const total = Number(data?.total || 0);
      setProducts(mergedProducts);
      setPagination({ total, totalPages: Math.max(1, Math.ceil(total / itemsPerPage)) });
      setServerStatusCounts(data?.status_counts || { all: total });
      setChangedData({});
      setChecklistState(nextChecklistState);
      setInitialChecklistState(nextChecklistState);
    } catch (err) {
      toast.error("Gagal memuat data produk PO");
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = async (productId = null) => {
    try {
      const url = productId ? `/admin/export/po-transactions?product_id=${productId}` : `/admin/export/po-transactions`;
      const response = await API.get(url, { responseType: 'blob' });
      const dlUrl = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = dlUrl;
      link.setAttribute('download', productId ? `po_transactions_${productId}.xlsx` : `po_transactions_global.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
    } catch (err) {
      toast.error("Gagal mengunduh Excel");
    }
  };

  const handleDownloadPdf = async (productId) => {
    try {
      const params = new URLSearchParams();
      params.set("product_id", productId);
      const response = await API.get(`/admin/po-monitoring/purchase-order-pdf?${params.toString()}`, { responseType: "blob" });
      const blobUrl = window.URL.createObjectURL(new Blob([response.data], { type: "application/pdf" }));
      const link = document.createElement("a");
      link.href = blobUrl;
      const disposition = response.headers?.["content-disposition"] || "";
      const filenameMatch = disposition.match(/filename=([^;]+)/i);
      const filename = filenameMatch?.[1]?.replace(/["']/g, "") || `purchase_order_${productId}.pdf`;
      link.setAttribute("download", filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(blobUrl);
    } catch (err) {
      toast.error("Gagal mengunduh PDF PO");
    }
  };

  const handleBroadcastPreview = async (product) => {
    try {
      setBroadcastingId(product.id);
      const { data } = await API.get(`/admin/po-monitoring/${product.id}/broadcast-preview`);
      const count = Number(data?.recipient_count || 0);
      const sample = (data?.recipients || []).slice(0, 5).map((item) => `${item.name} <${item.email}>`).join("\n");
      window.alert(`Preview broadcast email Ready di Pusat\n\nProduk: ${product.name}\nPenerima: ${count} email\n\nSample:\n${sample || "-"}\n\nSubject:\n${data?.subject || "-"}`);
    } catch (err) {
      toast.error(formatApiError(err, "Gagal membuat preview broadcast"));
    } finally {
      setBroadcastingId(null);
    }
  };

  const handleBroadcastSend = async (product) => {
    if (!window.confirm(`Simpan dry-run broadcast Ready di Pusat untuk ${product.name}?`)) return;
    try {
      setBroadcastingId(product.id);
      const { data } = await API.post(`/admin/po-monitoring/${product.id}/broadcast-ready`);
      toast.success(`Broadcast ${data.status || "dry_run"}: ${data.recipient_count || 0} penerima`);
    } catch (err) {
      toast.error(formatApiError(err, "Gagal menyimpan broadcast"));
    } finally {
      setBroadcastingId(null);
    }
  };

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search.trim()), 350);
    return () => clearTimeout(timer);
  }, [search]);

  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearch, statusFilter]);

  useEffect(() => {
    fetchProducts();
  }, [currentPage, debouncedSearch, statusFilter]);

  const handleChange = (id, field, value) => {
    setChangedData(prev => ({
      ...prev,
      [id]: {
        ...(prev[id] || {}),
        [field]: value
      }
    }));
  };

  const fetchFullProductForSave = async (productId) => {
    const { data } = await API.get(`/products/${productId}`);
    return data || {};
  };

  const buildProductPayloadForSave = async (product, changes = {}) => {
    const fullProduct = await fetchFullProductForSave(product.id);
    const payload = {
      ...fullProduct,
      ...changes,
    };
    delete payload.admin_note;
    delete payload.monitoring;
    delete payload.ordered_variants;
    return payload;
  };

  const mergeUpdatedProductForList = (row, updatedProduct = {}) => {
    const { variants, monitoring, ordered_variants, ...listSafeProduct } = updatedProduct;
    return {
      ...row,
      ...listSafeProduct,
      monitoring: row.monitoring,
      ordered_variants: row.ordered_variants,
    };
  };

  const saveSingleProductField = async (product, field, nextValue, successMessage, errorMessage) => {
    const previousValue = getVal(product, field) ?? "";
    const existingDraft = changedData[product.id] || {};

    setChangedData((prev) => ({
      ...prev,
      [product.id]: {
        ...(prev[product.id] || {}),
        [field]: nextValue,
      },
    }));
    setSavingId(product.id);

    try {
      const payload = await buildProductPayloadForSave(product, {
        ...existingDraft,
        [field]: nextValue,
      });

      const response = await API.put(`/products/${product.id}`, payload);
      const updatedProduct = response.data || {};

      setProducts((prev) => sortProductsByClosePo(prev.map((row) => row.id === product.id ? mergeUpdatedProductForList(row, updatedProduct) : row)));

      setChangedData((prev) => {
        const current = prev[product.id] || {};
        const next = { ...current };
        delete next[field];
        if (Object.keys(next).length === 0) {
          const clone = { ...prev };
          delete clone[product.id];
          return clone;
        }
        return {
          ...prev,
          [product.id]: next,
        };
      });

      if (successMessage) toast.success(successMessage);
    } catch (err) {
      setChangedData((prev) => ({
        ...prev,
        [product.id]: {
          ...(prev[product.id] || {}),
          [field]: previousValue,
        },
      }));
      toast.error(formatApiError(err.response?.data?.detail) || errorMessage);
    } finally {
      setSavingId((prev) => (prev === product.id ? null : prev));
    }
  };

  const handleStatusAutoSave = async (product, nextStatusValue) => {
    await saveSingleProductField(product, "is_po_open", nextStatusValue, "Status PO berhasil disimpan", "Gagal menyimpan status PO");
  };

  const handleDateAutoSave = async (product, field, nextValue) => {
    const fieldLabels = {
      preorder_open_date: "Tanggal Open PO berhasil disimpan",
      preorder_closing_date: "Tanggal Close PO berhasil disimpan",
      preorder_delivery_estimate: "Tanggal Estimasi berhasil disimpan",
    };
    await saveSingleProductField(product, field, nextValue, fieldLabels[field], "Gagal menyimpan tanggal PO");
  };

  const buildChecklistItems = (item, section) => (
    (item.ordered_variants || []).map((variantItem) => ({
      ...variantItem,
      key: buildChecklistKey(item.id, section, variantItem.signature),
      rawSignature: variantItem.signature,
    }))
  );

  const getChecklistPayloadForItem = (item, section, sourceState) => {
    const payload = {};
    buildChecklistItems(item, section).forEach((variantItem) => {
      payload[variantItem.rawSignature] = !!sourceState[section]?.[variantItem.key];
    });
    return payload;
  };

  const hasChecklistChanges = (item) => {
    const currentOrder = JSON.stringify(getChecklistPayloadForItem(item, "order", checklistState));
    const initialOrder = JSON.stringify(getChecklistPayloadForItem(item, "order", initialChecklistState));
    const currentReceived = JSON.stringify(getChecklistPayloadForItem(item, "received", checklistState));
    const initialReceived = JSON.stringify(getChecklistPayloadForItem(item, "received", initialChecklistState));
    return currentOrder !== initialOrder || currentReceived !== initialReceived;
  };

  const syncChecklistStateForSavedItem = (savedItem) => {
    const orderPayload = savedItem.monitoring?.order_checklist || {};
    const receivedPayload = savedItem.monitoring?.received_checklist || {};
    const variantItems = savedItem.ordered_variants || [];

    setChecklistState((prev) => {
      const next = { order: { ...(prev.order || {}) }, received: { ...(prev.received || {}) } };
      Object.keys(next.order).forEach((key) => {
        if (key.startsWith(`${savedItem.id}-${CHECKLIST_PREFIX.order}-`)) delete next.order[key];
      });
      Object.keys(next.received).forEach((key) => {
        if (key.startsWith(`${savedItem.id}-${CHECKLIST_PREFIX.received}-`)) delete next.received[key];
      });
      variantItems.forEach((variantItem) => {
        next.order[buildChecklistKey(savedItem.id, "order", variantItem.signature)] = !!orderPayload[variantItem.signature];
        next.received[buildChecklistKey(savedItem.id, "received", variantItem.signature)] = !!receivedPayload[variantItem.signature];
      });
      return next;
    });

    setInitialChecklistState((prev) => {
      const next = { order: { ...(prev.order || {}) }, received: { ...(prev.received || {}) } };
      Object.keys(next.order).forEach((key) => {
        if (key.startsWith(`${savedItem.id}-${CHECKLIST_PREFIX.order}-`)) delete next.order[key];
      });
      Object.keys(next.received).forEach((key) => {
        if (key.startsWith(`${savedItem.id}-${CHECKLIST_PREFIX.received}-`)) delete next.received[key];
      });
      variantItems.forEach((variantItem) => {
        next.order[buildChecklistKey(savedItem.id, "order", variantItem.signature)] = !!orderPayload[variantItem.signature];
        next.received[buildChecklistKey(savedItem.id, "received", variantItem.signature)] = !!receivedPayload[variantItem.signature];
      });
      return next;
    });
  };

  const clearAutosaveTimer = (productId) => {
    if (autosaveTimersRef.current[productId]) {
      clearTimeout(autosaveTimersRef.current[productId]);
      delete autosaveTimersRef.current[productId];
    }
  };

  const persistChecklistOnly = async (item, sourceChecklistState, silentSuccess = true) => {
    clearAutosaveTimer(item.id);
    setSavingId(item.id);
    try {
      const response = await API.put(`/admin/po-monitoring/${item.id}`, {
        sent_to_central: item.monitoring?.sent_to_central || false,
        sent_to_central_at: item.monitoring?.sent_to_central_at || null,
        sent_to_central_note: item.monitoring?.sent_to_central_note || "",
        received_from_central: item.monitoring?.received_from_central || false,
        received_from_central_at: item.monitoring?.received_from_central_at || null,
        received_from_central_note: item.monitoring?.received_from_central_note || "",
        admin_note: changedData[item.id]?.admin_note ?? item.monitoring?.admin_note ?? "",
        order_checklist: getChecklistPayloadForItem(item, "order", sourceChecklistState),
        received_checklist: getChecklistPayloadForItem(item, "received", sourceChecklistState),
      });
      const updatedItem = response.data?.item;
      if (updatedItem) {
        const normalizedItem = {
          ...item,
          monitoring: updatedItem.monitoring,
          ordered_variants: updatedItem.ordered_variants || item.ordered_variants || [],
        };
        setProducts((prev) => prev.map((row) => row.id === item.id ? normalizedItem : row));
        syncChecklistStateForSavedItem(normalizedItem);
      }
      if (!silentSuccess) toast.success("Checklist berhasil disimpan");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Gagal menyimpan checklist");
    } finally {
      setSavingId((prev) => prev === item.id ? null : prev);
    }
  };

  const scheduleChecklistAutosave = (item, nextChecklistState) => {
    clearAutosaveTimer(item.id);
    autosaveTimersRef.current[item.id] = setTimeout(() => {
      persistChecklistOnly(item, nextChecklistState, true);
    }, 700);
  };

  const handleChecklistToggle = (item, section, key, checked) => {
    const nextChecklistState = {
      ...checklistState,
      [section]: {
        ...(checklistState[section] || {}),
        [key]: checked,
      },
    };
    setChecklistState(nextChecklistState);
    scheduleChecklistAutosave(item, nextChecklistState);
  };

  const handleSave = async (product) => {
    const changes = changedData[product.id];
    const hasDraftChanges = !!changes;
    const hasChecklistDraft = hasChecklistChanges(product);
    const hasMonitoringDraft = changes?.admin_note !== undefined;
    if (!hasDraftChanges && !hasChecklistDraft) return;

    setSavingId(product.id);
    try {
      const productPayload = await buildProductPayloadForSave(product, changes || {});
      await API.put(`/products/${product.id}`, productPayload);

      if (hasChecklistDraft || hasMonitoringDraft) {
        await API.put(`/admin/po-monitoring/${product.id}`, {
          sent_to_central: product.monitoring?.sent_to_central || false,
          sent_to_central_at: product.monitoring?.sent_to_central_at || null,
          sent_to_central_note: product.monitoring?.sent_to_central_note || "",
          received_from_central: product.monitoring?.received_from_central || false,
          received_from_central_at: product.monitoring?.received_from_central_at || null,
          received_from_central_note: product.monitoring?.received_from_central_note || "",
          admin_note: changes?.admin_note ?? product.monitoring?.admin_note ?? "",
          order_checklist: getChecklistPayloadForItem(product, "order", checklistState),
          received_checklist: getChecklistPayloadForItem(product, "received", checklistState),
        });
      }

      await fetchProducts();
      toast.success("Pengaturan PO berhasil disimpan");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Gagal menyimpan pengaturan PO");
    } finally {
      setSavingId(null);
    }
  };

  const getVal = (p, field) => changedData[p.id]?.[field] ?? p[field] ?? "";
  const getPoSelectValue = (p) => {
    const effectiveValue = getPoStatusMeta(p, changedData[p.id] || {}).statusValue;
    const numericValue = Number(effectiveValue);
    return [0, 1, 2, 3, 4, 5].includes(numericValue) ? String(numericValue) : "1";
  };
  const getMonitoringVal = (p, field) => changedData[p.id]?.[field] ?? p.monitoring?.[field] ?? "";

  const statusFilterOptions = [
    { value: "all", label: "Semua" },
    { value: "1", label: "Open" },
    { value: "0", label: "Closed" },
    { value: "4", label: "Dikirim ke Pusat" },
    { value: "3", label: "Production" },
    { value: "2", label: "Ready di Pusat" },
    { value: "5", label: "Ready di Gallery" },
    { value: "no_orders", label: "Belum Ada Order" },
  ];

  const getProductEffectiveStatus = (p) => getPoStatusMeta(p, changedData[p.id] || {}).statusValue;

  const statusCounts = serverStatusCounts || { all: 0 };

  const filteredProducts = products;
  const searchKeyword = debouncedSearch.trim();
  const pageStart = pagination.total === 0 ? 0 : ((currentPage - 1) * itemsPerPage) + 1;
  const pageEnd = Math.min(currentPage * itemsPerPage, pagination.total);
  const activeStatusLabel = statusFilterOptions.find((option) => option.value === statusFilter)?.label;

  const toggleDesktopRow = (productId) => {
    setExpandedDesktopRows((prev) => ({
      ...prev,
      [productId]: !prev[productId],
    }));
  };

  const buildClosePoTemplate = (items = []) => {
    const grouped = new Map();
    items
      .filter((product) => product.preorder_closing_date && getPoStatusMeta(product).statusValue === 1)
      .forEach((product) => {
        const dateLabel = formatPoCloseDateForTemplate(product.preorder_closing_date);
        const dateKey = getPoCloseDateKey(product.preorder_closing_date);
        if (!grouped.has(dateKey)) grouped.set(dateKey, { dateLabel, items: [] });
        grouped.get(dateKey).items.push(product);
      });

    const sections = Array.from(grouped.entries())
      .sort(([aKey], [bKey]) => aKey.localeCompare(bKey))
      .map(([dateKey, group]) => {
        const scarcityLine = getClosePoScarcityLine(dateKey);
        return [
          `‼️ *INFO CLOSE PO/OO ${group.dateLabel}* ‼️`,
          ...(scarcityLine ? [scarcityLine] : []),
          ...group.items
            .sort((a, b) => getPoDisplayName(a).localeCompare(getPoDisplayName(b)))
            .map((product) => `* PO ${getPoDisplayName(product)}`),
        ].join("\n");
      });

    return [
      sections.length > 0 ? sections.join("\n\n") : ["‼️ *INFO CLOSE PO/OO* ‼️", "* PO -"].join("\n"),
      "",
      "*_Note_* :",
      "Kaka-kaka cantik, mohon perhatikan warna dan size saat order yaa🥰",
      "Dan mohon diperhatikan saat me-list orderan SEBELUM TANGGAL CLOSE PO, khawatir ada yang terlewat🌟🙏",
    ].join("\n");
  };

  const handleCopyClosePoTemplate = async () => {
    try {
      const params = new URLSearchParams();
      params.set("limit", "500");
      const { data } = await API.get(`/admin/po-monitoring?${params.toString()}`);
      const openProducts = (data?.items || []).map((item) => ({
        id: item.product_id,
        name: item.product_name,
        brand_id: item.brand_id,
        brand_name: item.brand_name,
        stock_type: "preorder",
        preorder_open_date: item.preorder_open_date,
        preorder_closing_date: item.preorder_closing_date,
        preorder_delivery_estimate: item.preorder_delivery_estimate,
        is_po_open: item.is_po_open,
        total_orders: item.total_orders,
        monitoring: item.monitoring,
        ordered_variants: item.ordered_variants || [],
      }));

      await navigator.clipboard.writeText(buildClosePoTemplate(openProducts));
      toast.success("Template info close PO berhasil disalin");
    } catch {
      toast.error("Gagal menyalin template info close PO");
    }
  };

  return (
    <AdminLayout title="Pengaturan PO">
      <div className="max-w-[1400px] mx-auto p-4 sm:p-6 lg:p-8 space-y-6">

        <div className="flex flex-col gap-3 bg-white p-4 rounded-xl border border-stone-100 shadow-sm">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div className="min-w-0">
              <h1 className="font-serif text-2xl text-stone-900">Pengaturan PO</h1>
              <p className="mt-1 text-sm text-stone-500">Kelola tanggal PO, status produk, checklist varian, dan catatan admin dalam satu tempat.</p>
            </div>
            <div className="flex flex-col sm:flex-row items-stretch gap-2 w-full sm:w-auto">
              <button onClick={handleCopyClosePoTemplate} className="flex-1 sm:flex-none inline-flex items-center justify-center gap-2 p-2 px-4 bg-[#8A9A86] text-white rounded shadow-sm hover:bg-[#768573] transition-colors text-sm font-medium text-center">
                <Copy className="w-4 h-4 shrink-0" /> <span className="leading-tight">Copy Template Close PO</span>
              </button>
              <button onClick={() => handleDownload()} className="flex-1 sm:flex-none inline-flex items-center justify-center gap-2 p-2 px-4 bg-emerald-600 text-white rounded shadow-sm hover:bg-emerald-700 transition-colors text-sm font-medium text-center">
                <Download className="w-4 h-4 shrink-0" /> <span className="leading-tight">Download Semua Laporan PO</span>
              </button>
              <button onClick={fetchProducts} disabled={loading} className="p-2 border border-stone-200 rounded text-stone-600 hover:bg-stone-50 disabled:opacity-50 shrink-0">
                <RefreshCw className={`w-5 h-5 ${loading ? "animate-spin" : ""}`} />
              </button>
            </div>
          </div>
          <div className="flex flex-col gap-3">
            <div className="relative w-full sm:max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Cari nama produk atau brand..."
                className="w-full h-10 pl-9 pr-3 rounded-lg border border-stone-200 bg-white text-sm outline-none focus:border-[#8A9A86]"
              />
            </div>
            <div className="space-y-2">
              <div className="flex flex-wrap gap-2">
                {statusFilterOptions.map((option) => {
                  const active = statusFilter === option.value;
                  return (
                    <button
                      key={option.value}
                      onClick={() => setStatusFilter(option.value)}
                      className={`inline-flex items-center gap-2 px-3 py-2 rounded-full border text-sm font-medium transition-colors ${active ? "border-[#8A9A86] bg-[#8A9A86] text-white" : option.value === "no_orders" ? "border-amber-200 bg-amber-50 text-amber-700 hover:bg-amber-100" : "border-stone-200 bg-white text-stone-600 hover:bg-stone-50"}`}
                    >
                      <span>{option.label}</span>
                      <span className={`inline-flex min-w-6 justify-center rounded-full px-1.5 py-0.5 text-xs ${active ? "bg-white/20 text-white" : "bg-stone-100 text-stone-600"}`}>
                        {statusCounts[option.value] || 0}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>
            <p className="text-xs text-stone-500">
              Menampilkan <span className="font-semibold text-stone-700">{pageStart}-{pageEnd}</span> dari <span className="font-semibold text-stone-700">{pagination.total}</span> produk preorder{activeStatusLabel && statusFilter !== "all" ? ` status ${activeStatusLabel}` : ""}{searchKeyword ? ` untuk pencarian “${searchKeyword}”` : ""}.
            </p>
          </div>
        </div>

        <div className="space-y-3 md:hidden">
          {loading && products.length === 0 ? (
            <div className="bg-white border border-stone-200 rounded-xl p-6 text-center text-stone-400">Loading...</div>
          ) : products.length === 0 ? (
            <div className="bg-white border border-stone-200 rounded-xl p-6 text-center text-stone-500">Tidak ada produk berstatus Pre-Order.</div>
          ) : filteredProducts.length === 0 ? (
            <div className="bg-white border border-stone-200 rounded-xl p-6 text-center text-stone-500">Tidak ada produk yang cocok dengan pencarian.</div>
          ) : (
            filteredProducts.map((p, index) => {
              const hasChanges = !!changedData[p.id] || hasChecklistChanges(p);
              const isSaving = savingId === p.id;
              const { statusValue, statusClass, statusLabel, selectOpenLabel } = getPoStatusMeta(p, changedData[p.id] || {});
              const isClosedAttention = statusValue === 0;

              return (
                <div key={p.id} className={`border rounded-xl shadow-sm p-4 space-y-4 ${isClosedAttention ? "bg-red-50 border-red-200" : "bg-white border-stone-200"}`}>
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <p className="text-xs text-stone-400">#{(currentPage - 1) * itemsPerPage + index + 1} · PO</p>
                      <h3 className="font-semibold text-stone-900 leading-snug break-words">{p.name}</h3>
                      <p className="text-sm text-stone-500 mt-1">{p.brand_name || p.brand_id?.replace("brand_", "") || "No Brand"}</p>
                    </div>
                    <span className={`shrink-0 inline-flex px-2.5 py-1 rounded-full text-[11px] font-semibold border ${statusClass}`}>{statusLabel}</span>
                  </div>

                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <div className="rounded-lg bg-stone-50 border border-stone-100 p-3">
                      <p className="text-[11px] uppercase tracking-wide text-stone-400">Jumlah Order</p>
                      <p className="mt-1 font-semibold text-stone-900">{p.total_orders || 0}</p>
                    </div>
                    <div className="rounded-lg bg-stone-50 border border-stone-100 p-3">
                      <p className="text-[11px] uppercase tracking-wide text-stone-400">Status</p>
                      <p className="mt-1 font-semibold text-stone-900">{statusLabel}</p>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 gap-3">
                    <div>
                      <label className="text-[11px] uppercase tracking-wide text-stone-400">Open PO</label>
                      <input
                        type="date"
                        value={getVal(p, "preorder_open_date")}
                        onChange={(e) => handleDateAutoSave(p, "preorder_open_date", e.target.value)}
                        className={`mt-1 w-full px-3 py-2.5 border rounded-lg text-sm focus:ring-2 focus:ring-[#8A9A86] focus:border-transparent outline-none ${changedData[p.id]?.preorder_open_date !== undefined ? "border-amber-300 bg-amber-50" : "border-stone-200"}`}
                      />
                    </div>
                    <div>
                      <label className="text-[11px] uppercase tracking-wide text-stone-400">Close PO (WIB)</label>
                      <input
                        type="datetime-local"
                        value={normalizeDateTimeLocalValue(getVal(p, "preorder_closing_date"))}
                        onChange={(e) => handleDateAutoSave(p, "preorder_closing_date", e.target.value)}
                        className={`mt-1 w-full px-3 py-2.5 border rounded-lg text-sm focus:ring-2 focus:ring-[#8A9A86] focus:border-transparent outline-none ${changedData[p.id]?.preorder_closing_date !== undefined ? "border-amber-300 bg-amber-50" : "border-stone-200"}`}
                      />
                    </div>
                    <div>
                      <label className="text-[11px] uppercase tracking-wide text-stone-400">Estimasi</label>
                      <input
                        type="date"
                        value={getVal(p, "preorder_delivery_estimate")}
                        onChange={(e) => handleDateAutoSave(p, "preorder_delivery_estimate", e.target.value)}
                        className={`mt-1 w-full px-3 py-2.5 border rounded-lg text-sm focus:ring-2 focus:ring-[#8A9A86] focus:border-transparent outline-none ${changedData[p.id]?.preorder_delivery_estimate !== undefined ? "border-amber-300 bg-amber-50" : "border-stone-200"}`}
                      />
                    </div>
                    <div>
                      <label className="text-[11px] uppercase tracking-wide text-stone-400">Keterangan</label>
                      <select
                        value={getPoSelectValue(p)}
                        onChange={(e) => handleStatusAutoSave(p, Number(e.target.value))}
                        className={`mt-1 w-full px-3 py-2.5 border rounded-lg text-sm focus:ring-2 focus:ring-[#8A9A86] outline-none cursor-pointer ${
                          statusValue === 5 ? "bg-fuchsia-100 border-fuchsia-300 text-fuchsia-800" :
                          statusValue === 2 ? "bg-emerald-100 border-emerald-300 text-emerald-800" :
                          statusValue === 3 ? "bg-indigo-100 border-indigo-300 text-indigo-800" :
                          statusValue === 4 ? "bg-cyan-100 border-cyan-300 text-cyan-800" :
                          statusValue === 0 ? "bg-red-100 border-red-300 text-red-800" :
                          "bg-white border-stone-200 text-stone-800"
                        }`}
                      >
                        <option value="1">{selectOpenLabel}</option>
                        <option value="0">Closed</option>
                        <option value="4">Dikirim ke Pusat</option>
                        <option value="3">Production</option>
                        <option value="2">Ready di Pusat</option>
                        <option value="5">Ready di Gallery</option>
                      </select>
                    </div>
                  </div>

                  <div className="space-y-3">
                    <VariantChecklistAccordion
                      title="Checklist Varian Order"
                      items={buildChecklistItems(p, "order")}
                      checkedMap={checklistState.order}
                      onToggle={(key, checked) => handleChecklistToggle(p, "order", key, checked)}
                    />
                    <VariantChecklistAccordion
                      title="Checklist Barang Datang"
                      items={buildChecklistItems(p, "received")}
                      checkedMap={checklistState.received}
                      onToggle={(key, checked) => handleChecklistToggle(p, "received", key, checked)}
                      emptyLabel="Belum ada checklist barang datang."
                    />
                    <div>
                      <label className="text-[11px] uppercase tracking-wide text-stone-400">Notes Admin Barang Kurang</label>
                      <textarea
                        value={getMonitoringVal(p, "admin_note")}
                        onChange={(e) => handleChange(p.id, "admin_note", e.target.value)}
                        placeholder="Contoh: warna hitam size L kurang 2 pcs, menunggu kiriman susulan"
                        rows={3}
                        className={`mt-1 w-full px-3 py-2.5 border rounded-lg text-sm focus:ring-2 focus:ring-[#8A9A86] focus:border-transparent outline-none resize-y ${changedData[p.id]?.admin_note !== undefined ? "border-amber-300 bg-amber-50" : "border-stone-200"}`}
                      />
                    </div>
                    <p className="text-[11px] text-stone-400">Checklist tersimpan otomatis beberapa saat setelah diubah. Catatan admin tersimpan saat tombol Simpan ditekan.</p>
                  </div>

                  <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
                    <button
                      onClick={() => handleDownload(p.id)}
                      className="inline-flex items-center justify-center gap-2 px-3 py-2.5 rounded-lg bg-emerald-50 text-emerald-700 border border-emerald-200 text-sm font-medium"
                    >
                      <Download className="w-4 h-4" />
                      Excel
                    </button>
                    <button
                      onClick={() => handleDownloadPdf(p.id)}
                      className="inline-flex items-center justify-center gap-2 px-3 py-2.5 rounded-lg bg-blue-50 text-blue-700 border border-blue-200 text-sm font-medium"
                    >
                      <FileText className="w-4 h-4" />
                      PDF PO
                    </button>
                    <button
                      onClick={() => handleBroadcastPreview(p)}
                      disabled={broadcastingId === p.id}
                      className="inline-flex items-center justify-center gap-2 px-3 py-2.5 rounded-lg bg-amber-50 text-amber-700 border border-amber-200 text-sm font-medium disabled:opacity-60"
                    >
                      <Mail className="w-4 h-4" />
                      Preview
                    </button>
                    <button
                      onClick={() => handleBroadcastSend(p)}
                      disabled={broadcastingId === p.id}
                      className="inline-flex items-center justify-center gap-2 px-3 py-2.5 rounded-lg bg-purple-50 text-purple-700 border border-purple-200 text-sm font-medium disabled:opacity-60"
                    >
                      <Mail className="w-4 h-4" />
                      Dry Run
                    </button>
                    <button
                      onClick={() => handleSave(p)}
                      disabled={!hasChanges || isSaving}
                      className={`inline-flex items-center justify-center gap-2 px-3 py-2.5 rounded-lg text-sm font-medium ${hasChanges && !isSaving ? "bg-[#8A9A86] text-white" : "bg-stone-200 text-stone-500 border border-stone-300"}`}
                    >
                      {isSaving ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                      {isSaving ? "Menyimpan..." : "Simpan"}
                    </button>
                  </div>
                </div>
              );
            })
          )}
        </div>

        <div className="hidden md:block bg-white border text-sm border-stone-200 rounded-xl shadow-sm overflow-hidden overflow-x-auto w-full">
          <table className="w-full text-left min-w-[1000px]">
            <thead>
              <tr className="border-b border-stone-200 bg-[#8A9A86]/20">
                <th className="p-3 font-semibold text-stone-800 w-12 text-center">NO</th>
                <th className="p-3 font-semibold text-stone-800 w-16">O/F</th>
                <th className="p-3 font-semibold text-stone-800 min-w-[200px]">PRODUK PO/OO</th>
                <th className="p-3 font-semibold text-stone-800 w-48">BRAND</th>
                <th className="p-3 font-semibold text-stone-800 w-24 text-center">JML ORDER</th>
                <th className="p-3 font-semibold text-stone-800 w-40">OPEN PO</th>
                <th className="p-3 font-semibold text-stone-800 w-40">CLOSE PO</th>
                <th className="p-3 font-semibold text-stone-800 w-40">ESTIMASI</th>
                <th className="p-3 font-semibold text-stone-800 w-32">KET</th>
                <th className="p-3 font-semibold text-stone-800 w-16">Aksi</th>
              </tr>
            </thead>
            <tbody>
              {loading && products.length === 0 ? (
                <tr>
                  <td colSpan={10} className="text-center py-10 text-stone-400">Loading...</td>
                </tr>
              ) : products.length === 0 ? (
                <tr>
                  <td colSpan={10} className="text-center py-10 text-stone-500">Tidak ada produk berstatus Pre-Order.</td>
                </tr>
              ) : filteredProducts.length === 0 ? (
                <tr>
                  <td colSpan={10} className="text-center py-10 text-stone-500">Tidak ada produk yang cocok dengan pencarian.</td>
                </tr>
              ) : (
                filteredProducts.map((p, index) => {
                  const hasChanges = !!changedData[p.id] || hasChecklistChanges(p);
                  const isSaving = savingId === p.id;
                  const isExpanded = !!expandedDesktopRows[p.id];
                  const { statusValue, selectOpenLabel } = getPoStatusMeta(p, changedData[p.id] || {});
                  const isClosedAttention = statusValue === 0;

                  return (
                    <Fragment key={p.id}>
                      <tr className={`border-b transition-colors ${isClosedAttention ? "bg-red-50 border-red-100 hover:bg-red-100/70" : "border-stone-100 hover:bg-stone-50/50"}`}>
                        <td className="p-3 text-center text-stone-500 font-mono text-xs">{(currentPage - 1) * itemsPerPage + index + 1}</td>
                        <td className="p-3 text-stone-600">PO</td>
                        <td className="p-3 font-medium text-stone-900">{p.name}</td>
                        <td className="p-3 text-stone-600">{p.brand_name || p.brand_id?.replace("brand_", "")}</td>

                        <td className="p-3 text-center font-semibold text-stone-700 bg-stone-50/50">
                          {p.total_orders || 0}
                        </td>

                        <td className="p-2">
                          <input
                            type="date"
                            value={getVal(p, "preorder_open_date")}
                            onChange={(e) => handleDateAutoSave(p, "preorder_open_date", e.target.value)}
                            className={`w-full px-2 py-1.5 border rounded text-xs focus:ring-2 focus:ring-[#8A9A86] focus:border-transparent outline-none ${changedData[p.id]?.preorder_open_date !== undefined ? "border-amber-300 bg-amber-50" : "border-stone-200"}`}
                          />
                        </td>

                        <td className="p-2">
                          <input
                            type="datetime-local"
                            value={normalizeDateTimeLocalValue(getVal(p, "preorder_closing_date"))}
                            onChange={(e) => handleDateAutoSave(p, "preorder_closing_date", e.target.value)}
                            className={`w-full px-2 py-1.5 border rounded text-xs focus:ring-2 focus:ring-[#8A9A86] focus:border-transparent outline-none ${changedData[p.id]?.preorder_closing_date !== undefined ? "border-amber-300 bg-amber-50" : "border-stone-200"}`}
                          />
                        </td>

                        <td className="p-2">
                          <input
                            type="date"
                            value={getVal(p, "preorder_delivery_estimate")}
                            onChange={(e) => handleDateAutoSave(p, "preorder_delivery_estimate", e.target.value)}
                            className={`w-full px-2 py-1.5 border rounded text-xs focus:ring-2 focus:ring-[#8A9A86] focus:border-transparent outline-none ${changedData[p.id]?.preorder_delivery_estimate !== undefined ? "border-amber-300 bg-amber-50" : "border-stone-200"}`}
                          />
                        </td>

                        <td className="p-2">
                          <select
                            value={getPoSelectValue(p)}
                            onChange={(e) => handleStatusAutoSave(p, Number(e.target.value))}
                            className={`w-full px-2 py-1.5 border rounded text-xs focus:ring-2 focus:ring-[#8A9A86] outline-none cursor-pointer ${
                              statusValue === 5 ? "bg-fuchsia-100 border-fuchsia-300 text-fuchsia-800" :
                              statusValue === 2 ? "bg-emerald-100 border-emerald-300 text-emerald-800" :
                              statusValue === 3 ? "bg-indigo-100 border-indigo-300 text-indigo-800" :
                              statusValue === 4 ? "bg-cyan-100 border-cyan-300 text-cyan-800" :
                              statusValue === 0 ? "bg-red-100 border-red-300 text-red-800" :
                              "bg-white border-stone-200 text-stone-800"
                            }`}
                          >
                            <option value="1">{selectOpenLabel}</option>
                            <option value="0">Closed</option>
                            <option value="4">Dikirim ke Pusat</option>
                            <option value="3">Production</option>
                            <option value="2">Ready di Pusat</option>
                            <option value="5">Ready di Gallery</option>
                          </select>
                        </td>

                        <td className="p-2 h-full align-middle">
                          <div className="flex items-center gap-1 justify-center">
                            <button
                              onClick={() => toggleDesktopRow(p.id)}
                              className={`p-1.5 rounded shadow transition-colors flex items-center justify-center w-8 h-8 ${isExpanded ? "bg-stone-700 text-white hover:bg-stone-800" : "bg-stone-100 text-stone-700 hover:bg-stone-200"}`}
                              title="Buka checklist & catatan"
                            >
                              <ChevronDown className={`w-4 h-4 transition-transform ${isExpanded ? "rotate-180" : ""}`} />
                            </button>
                            <button
                              onClick={() => handleDownload(p.id)}
                              className="bg-emerald-100 text-emerald-700 p-1.5 rounded shadow hover:bg-emerald-200 transition-colors flex items-center justify-center w-8 h-8"
                              title="Unduh Excel PO produk ini"
                            >
                              <Download className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => handleDownloadPdf(p.id)}
                              className="bg-blue-100 text-blue-700 p-1.5 rounded shadow hover:bg-blue-200 transition-colors flex items-center justify-center w-8 h-8"
                              title="Unduh PDF PO produk ini"
                            >
                              <FileText className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => handleBroadcastPreview(p)}
                              disabled={broadcastingId === p.id}
                              className="bg-amber-100 text-amber-700 p-1.5 rounded shadow hover:bg-amber-200 transition-colors flex items-center justify-center w-8 h-8 disabled:opacity-60"
                              title="Preview broadcast email Ready di Pusat"
                            >
                              <Mail className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => handleBroadcastSend(p)}
                              disabled={broadcastingId === p.id}
                              className="bg-purple-100 text-purple-700 p-1.5 rounded shadow hover:bg-purple-200 transition-colors flex items-center justify-center w-8 h-8 disabled:opacity-60"
                              title="Simpan dry-run broadcast Ready di Pusat"
                            >
                              <Mail className="w-4 h-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                      {isExpanded && (
                        <tr className="border-b border-stone-100 bg-stone-50/60">
                          <td colSpan={10} className="p-4">
                            <div className="space-y-3">
                              <VariantChecklistAccordion
                                title="Checklist Varian Order"
                                items={buildChecklistItems(p, "order")}
                                checkedMap={checklistState.order}
                                onToggle={(key, checked) => handleChecklistToggle(p, "order", key, checked)}
                              />
                              <VariantChecklistAccordion
                                title="Checklist Barang Datang"
                                items={buildChecklistItems(p, "received")}
                                checkedMap={checklistState.received}
                                onToggle={(key, checked) => handleChecklistToggle(p, "received", key, checked)}
                                emptyLabel="Belum ada checklist barang datang."
                              />
                              <div>
                                <label className="text-[11px] uppercase tracking-wide text-stone-400">Notes Admin Barang Kurang</label>
                                <textarea
                                  value={getMonitoringVal(p, "admin_note")}
                                  onChange={(e) => handleChange(p.id, "admin_note", e.target.value)}
                                  placeholder="Contoh: warna hitam size L kurang 2 pcs, menunggu kiriman susulan"
                                  rows={3}
                                  className={`mt-1 w-full px-3 py-2.5 border rounded-lg text-sm focus:ring-2 focus:ring-[#8A9A86] focus:border-transparent outline-none resize-y ${changedData[p.id]?.admin_note !== undefined ? "border-amber-300 bg-amber-50" : "border-stone-200"}`}
                                />
                              </div>
                              <div className="flex flex-col gap-3 pt-1 sm:flex-row sm:items-center sm:justify-between">
                                <p className="text-[11px] text-stone-400">Checklist tersimpan otomatis beberapa saat setelah diubah. Catatan admin tersimpan saat tombol Simpan ditekan.</p>
                                <button
                                  onClick={() => handleSave(p)}
                                  disabled={!hasChanges || isSaving}
                                  className={`inline-flex items-center justify-center gap-2 px-3 py-2 rounded-lg text-sm font-medium ${hasChanges && !isSaving ? "bg-[#8A9A86] text-white hover:bg-[#72826e]" : "bg-stone-200 text-stone-500 border border-stone-300 cursor-not-allowed"}`}
                                  title={hasChanges ? "Simpan perubahan" : "Belum ada perubahan"}
                                >
                                  {isSaving ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                                  {isSaving ? "Menyimpan..." : "Simpan"}
                                </button>
                              </div>
                            </div>
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
        {!loading && pagination.totalPages > 1 && (
          <div className="flex flex-col sm:flex-row items-center justify-between gap-3 bg-white border border-stone-200 rounded-xl px-4 py-3 text-sm text-stone-600">
            <p>Halaman {currentPage} dari {pagination.totalPages}</p>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setCurrentPage((page) => Math.max(1, page - 1))}
                disabled={currentPage <= 1}
                className="inline-flex items-center gap-1 px-3 py-2 border border-stone-200 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-stone-50"
              >
                <ChevronLeft className="w-4 h-4" /> Prev
              </button>
              <button
                onClick={() => setCurrentPage((page) => Math.min(pagination.totalPages, page + 1))}
                disabled={currentPage >= pagination.totalPages}
                className="inline-flex items-center gap-1 px-3 py-2 border border-stone-200 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-stone-50"
              >
                Next <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
